import PublicSiteLayout from "@/components/landing/PublicSiteLayout";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const sectionOrder = [
  "purpose",
  "optInMethods",
  "disclosure",
  "messageTypes",
  "exampleFlow",
  "evidence",
  "controls",
  "restrictions",
  "relatedPages",
] as const;

type SectionKey = (typeof sectionOrder)[number];
type ProofItem = { title: string; body: string };
type EvidenceCard = { title: string; caption: string; imageSrc?: string; imageAlt?: string };

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

function asItemArray(value: unknown): ProofItem[] {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is ProofItem =>
          typeof entry === "object" &&
          entry !== null &&
          typeof (entry as ProofItem).title === "string" &&
          typeof (entry as ProofItem).body === "string",
      )
    : [];
}

function asEvidenceArray(value: unknown): EvidenceCard[] {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is EvidenceCard =>
          typeof entry === "object" &&
          entry !== null &&
          typeof (entry as { title: string }).title === "string" &&
          typeof (entry as { caption: string }).caption === "string",
      )
    : [];
}

function EvidenceCard({ card }: { card: EvidenceCard }) {
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <div className="rounded-2xl border border-[#CBD8EA] bg-[#F8FBFF] p-4">
      {card.imageSrc && !imageFailed ? (
        <div className="overflow-hidden rounded-xl border border-[#D9E4F3] bg-white">
          <img
            src={card.imageSrc}
            alt={card.imageAlt || card.title}
            className="h-52 w-full object-cover object-top"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </div>
      ) : (
        <div className="flex min-h-40 items-center justify-center rounded-xl border border-[#D9E4F3] bg-white px-4 text-center text-sm leading-6 text-muted-foreground">
          This evidence section describes the WorkScope consent control shown in the app.
        </div>
      )}
      <h3 className="mt-4 text-sm font-semibold text-primary">{card.title}</h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{card.caption}</p>
    </div>
  );
}

export default function SmsOptInProof() {
  const { t } = useTranslation();

  const sections = sectionOrder.map((key) => ({
    key,
    title: t(`smsOptInProofPage.sections.${key}.title`),
    paragraphs: asStringArray(
      t(`smsOptInProofPage.sections.${key}.paragraphs`, {
        returnObjects: true,
      }),
    ),
    bullets: asStringArray(
      t(`smsOptInProofPage.sections.${key}.bullets`, {
        returnObjects: true,
      }),
    ),
    steps: asStringArray(
      t(`smsOptInProofPage.sections.${key}.steps`, {
        returnObjects: true,
      }),
    ),
    items: asItemArray(
      t(`smsOptInProofPage.sections.${key}.items`, {
        returnObjects: true,
      }),
    ),
  }));

  const evidenceCards = asEvidenceArray(
    t("smsOptInProofPage.evidenceCards", {
      returnObjects: true,
    }),
  );

  return (
    <PublicSiteLayout mainClassName="bg-[linear-gradient(180deg,#F7FAFF_0%,#FFFFFF_18%,#FFFFFF_100%)] py-12 lg:py-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mx-auto max-w-4xl overflow-hidden rounded-[1.75rem] border border-[#D8E3F4] bg-card shadow-[0_32px_90px_-54px_rgba(12,31,79,0.3)]">
          <div className="border-b border-[#E6EDF8] bg-[linear-gradient(135deg,#0C1F4F_0%,#14306F_100%)] px-6 py-8 lg:px-10">
            <p className="text-sm font-medium text-accent">
              {t("smsOptInProofPage.eyebrow")}
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white lg:text-4xl">
              {t("smsOptInProofPage.title")}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-white">
              {t("smsOptInProofPage.subtitle")}
            </p>
            <div className="mt-5 text-sm text-white/80">
              <p>{t("smsOptInProofPage.lastUpdated")}</p>
            </div>
          </div>

          <div className="px-6 py-8 lg:px-10 lg:py-10">
            <div className="space-y-10">
              {sections.map((section, index) => (
                <section key={section.key} className="space-y-4">
                  <h2 className="text-xl font-bold text-primary lg:text-2xl">
                    {index + 1}) {section.title}
                  </h2>

                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-7 text-muted-foreground">
                      {paragraph}
                    </p>
                  ))}

                  {section.items?.length ? (
                    <div className="space-y-4">
                      {section.items.map((item, itemIndex) => (
                        <div
                          key={`${item.title}-${itemIndex}`}
                          className="rounded-2xl border border-border/70 bg-[#FAFCFF] px-5 py-4"
                        >
                          <h3 className="text-sm font-semibold text-primary">
                            {itemIndex + 1}. {item.title}
                          </h3>
                          <p className="mt-2 text-sm leading-7 text-muted-foreground">
                            {item.body}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {section.key === "disclosure" ? (
                    <div className="rounded-2xl border border-[#F6D9A6] bg-[#FFF8E8] px-5 py-5">
                      <blockquote className="text-sm leading-7 text-[#5C3B03]">
                        {t("smsOptInProofPage.sampleDisclosure")}
                      </blockquote>
                    </div>
                  ) : null}

                  {section.bullets?.length ? (
                    <ul className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="list-disc">
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {section.steps?.length ? (
                    <ol className="space-y-3">
                      {section.steps.map((step, stepIndex) => (
                        <li
                          key={step}
                          className="rounded-2xl border border-border/70 bg-[#FAFCFF] px-5 py-4 text-sm leading-7 text-muted-foreground"
                        >
                          <span className="mr-2 font-semibold text-primary">
                            {stepIndex + 1}.
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  ) : null}

                  {section.key === "evidence" ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {evidenceCards.map((card) => (
                        <EvidenceCard key={card.title} card={card} />
                      ))}
                    </div>
                  ) : null}

                  {section.key === "relatedPages" ? (
                    <div className="flex flex-wrap gap-3 text-sm">
                      <Link className="font-medium text-accent hover:underline" to="/privacy">
                        Privacy Policy
                      </Link>
                      <span className="text-muted-foreground">•</span>
                      <Link className="font-medium text-accent hover:underline" to="/terms">
                        Terms of Service
                      </Link>
                      <span className="text-muted-foreground">•</span>
                      <Link className="font-medium text-accent hover:underline" to="/sms-consent">
                        SMS Consent
                      </Link>
                    </div>
                  ) : null}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PublicSiteLayout>
  );
}
